/**
 * One extension's download button, and for admins, where it comes from.
 *
 * There are two extensions (queued and connected) and each has its own link:
 * an admin either pastes a public URL or uploads the build's zip to their S3
 * bucket. Anyone signed in can download either.
 */
import { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type { ExtensionKind } from '../../types';

/** The setting holding each link. The queued one keeps the key it had before
 *  there were two, so a link already saved still works. */
export const DOWNLOAD_URL_KEYS: Record<ExtensionKind, string> = {
  queued: 'browser_plugin_download_url',
  connected: 'browser_extension_connected_download_url',
};

const NAMES: Record<ExtensionKind, string> = { queued: 'queued', connected: 'connected' };

/** `admin` off shows only the download button, as in the install guide. For an
 *  admin the link and upload are folded under "Publish a new build", since
 *  they are used once per release. */
export default function ExtensionDownload({ kind, admin = true }: { kind: ExtensionKind; admin?: boolean }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin && admin;
  const [url, setUrl] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [hasStorage, setHasStorage] = useState(false);
  const [busy, setBusy] = useState<'save' | 'upload' | null>(null);
  // The link reads as plain text until clicked; then it is a box with Save.
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    api
      .getPluginDownloadUrl(kind)
      .then((r) => {
        setUrl(r.url);
        setUrlInput(r.url ?? '');
      })
      .catch(() => setUrl(null));
    if (isAdmin) {
      api
        .getImageStorageSettings()
        .then((s) => setHasStorage((s.items || []).some((x) => x.is_enabled)))
        .catch(() => setHasStorage(false));
    }
  }, [kind, isAdmin]);

  const done = (saved: string | null, text: string) => {
    setUrl(saved);
    setUrlInput(saved ?? '');
    setEditing(false);
    setNote({ ok: true, text });
  };

  const cancel = () => {
    setUrlInput(url ?? '');
    setEditing(false);
  };

  const save = async () => {
    setBusy('save');
    setNote(null);
    try {
      const saved = await api.updateAdminSetting(DOWNLOAD_URL_KEYS[kind], urlInput.trim() || null);
      done(saved.value, 'Download link saved.');
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : 'Could not save the link' });
    } finally {
      setBusy(null);
    }
  };

  const upload = async (file: File) => {
    setBusy('upload');
    setNote(null);
    try {
      const saved = await api.uploadPluginZip(file, kind);
      done(saved.value, 'Uploaded and published.');
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : 'Upload failed' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3" data-testid={`extension-download-${kind}`}>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm"
        >
          Download the {NAMES[kind]} extension
        </a>
      ) : (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          No download link published yet{isAdmin ? ' - add one below.' : ' - ask an admin to publish it.'}
        </p>
      )}

      {isAdmin && (
        <details className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <summary className="cursor-pointer text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
            Publish a new build (admin)
          </summary>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Download link for the {NAMES[kind]} extension (admin)
          </label>
          {editing ? (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <input
                type="url"
                autoFocus
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') cancel();
                }}
                placeholder={`https://cdn.example.com/public/lumnian-${kind}-extension.zip`}
                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono"
              />
              <button
                type="submit"
                disabled={busy !== null}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
              >
                {busy === 'save' ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={cancel}
                className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:underline"
              >
                Cancel
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              title="Click to change the link"
              data-testid={`extension-link-${kind}`}
              className="group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 -mx-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <span
                className={`min-w-0 truncate font-mono text-xs ${
                  url ? 'text-gray-700 dark:text-gray-200' : 'italic text-gray-400 dark:text-gray-500'
                }`}
              >
                {url || 'No link set - click to add one'}
              </span>
              <span className="shrink-0 text-xs text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100">
                Edit
              </span>
            </button>
          )}
          <div className="flex items-center gap-3 mt-2">
            <label
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm border ${
                hasStorage && busy === null
                  ? 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
                  : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              {busy === 'upload' ? 'Uploading…' : 'Upload .zip to my S3'}
              <input
                type="file"
                accept=".zip,application/zip"
                className="hidden"
                data-testid={`extension-upload-${kind}`}
                disabled={!hasStorage || busy !== null}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void upload(f);
                  e.target.value = '';
                }}
              />
            </label>
            {!hasStorage && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Add an S3 bucket in Settings → Image Storage to upload here.
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            To get the zip: on GitHub, open Actions → "Package {NAMES[kind]} extension" → Run workflow,
            then download it from the finished run. Upload the{' '}
            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">lumnian-{kind}-extension-….zip</code> inside.
          </p>
          {note && (
            <p className={`text-xs mt-1 ${note.ok ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
              {note.text}
            </p>
          )}
        </details>
      )}
    </div>
  );
}
